/* SEQ Air Care: paid-search landing-page interactions */
'use strict';

(() => {
  const money = cents => new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    maximumFractionDigits: 2
  }).format(cents / 100);

  const pushDataLayer = (eventName, details = {}) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(Object.assign({ event: eventName }, details));
  };

  const initLanding = section => {
    if (!(section instanceof HTMLElement) || section.dataset.gadsReady === 'true') return;
    section.dataset.gadsReady = 'true';

    document.body.classList.add('seq-google-landing-template');

    const firstPrice = Number(section.dataset.firstPrice);
    const additionalPrice = Number(section.dataset.additionalPrice);
    const outdoorPrice = Number(section.dataset.outdoorPrice);
    const bookingUrl = section.dataset.bookingUrl || '/products/split-system-deep-clean';
    const builder = section.querySelector('#seq-gads-price');

    const indoorCount = section.querySelector('[data-gads-indoor-count]');
    const outdoorCount = section.querySelector('[data-gads-outdoor-count]');
    const total = section.querySelector('[data-gads-total]');
    const indoorLabel = section.querySelector('[data-gads-indoor-label]');
    const indoorTotal = section.querySelector('[data-gads-indoor-total]');
    const outdoorRow = section.querySelector('[data-gads-outdoor-row]');
    const outdoorLabel = section.querySelector('[data-gads-outdoor-label]');
    const outdoorTotal = section.querySelector('[data-gads-outdoor-total]');
    const saving = section.querySelector('[data-gads-saving]');
    const indoorMinus = section.querySelector('[data-gads-indoor-minus]');
    const indoorPlus = section.querySelector('[data-gads-indoor-plus]');
    const outdoorMinus = section.querySelector('[data-gads-outdoor-minus]');
    const outdoorPlus = section.querySelector('[data-gads-outdoor-plus]');

    let indoorUnits = 1;
    let outdoorUnits = 0;

    const indoorSubtotal = units => firstPrice + Math.max(0, units - 1) * additionalPrice;

    const updateBookingCtas = () => {
      section.querySelectorAll('[data-gads-booking-cta], .seq-reels [data-open-booking]').forEach(cta => {
        cta.dataset.bookingUnits = String(indoorUnits);
        cta.dataset.bookingOutdoor = String(outdoorUnits);

        try {
          const url = new URL(bookingUrl, window.location.origin);
          url.searchParams.set('units', String(indoorUnits));
          url.searchParams.set('outdoor', String(outdoorUnits));
          url.hash = 'seq-pdp-book';
          cta.href = url.origin === window.location.origin
            ? url.pathname + url.search + url.hash
            : url.toString();
        } catch (error) {
          cta.href = bookingUrl;
        }
      });
    };

    const update = () => {
      indoorUnits = Math.max(1, Math.min(10, indoorUnits));
      outdoorUnits = Math.max(0, Math.min(indoorUnits, outdoorUnits));

      const indoorAmount = indoorSubtotal(indoorUnits);
      const outdoorAmount = outdoorUnits * outdoorPrice;
      const totalAmount = indoorAmount + outdoorAmount;
      const multiUnitSaving = Math.max(0, indoorUnits - 1) * Math.max(0, firstPrice - additionalPrice);

      if (indoorCount) indoorCount.value = String(indoorUnits);
      if (outdoorCount) outdoorCount.value = String(outdoorUnits);
      if (total) total.textContent = money(totalAmount);
      if (indoorLabel) indoorLabel.textContent = indoorUnits + ' indoor deep clean' + (indoorUnits === 1 ? '' : 's');
      if (indoorTotal) indoorTotal.textContent = money(indoorAmount);
      if (outdoorRow) outdoorRow.hidden = outdoorUnits === 0;
      if (outdoorLabel) outdoorLabel.textContent = outdoorUnits + ' outdoor unit clean' + (outdoorUnits === 1 ? '' : 's');
      if (outdoorTotal) outdoorTotal.textContent = money(outdoorAmount);

      if (indoorMinus) indoorMinus.disabled = indoorUnits <= 1;
      if (indoorPlus) indoorPlus.disabled = indoorUnits >= 10;
      if (outdoorMinus) outdoorMinus.disabled = outdoorUnits <= 0;
      if (outdoorPlus) outdoorPlus.disabled = outdoorUnits >= indoorUnits;

      if (saving) {
        saving.hidden = indoorUnits < 2 || multiUnitSaving <= 0;
        saving.textContent = indoorUnits < 2
          ? ''
          : 'Multi-unit pricing saves ' + money(multiUnitSaving) + ' compared with separate visits.';
      }

      updateBookingCtas();

      section.dispatchEvent(new CustomEvent('seq:gads-selection-changed', {
        bubbles: false,
        detail: {
          indoorUnits,
          outdoorUnits,
          total: totalAmount
        }
      }));
    };

    indoorMinus?.addEventListener('click', () => {
      indoorUnits -= 1;
      if (outdoorUnits > indoorUnits) outdoorUnits = indoorUnits;
      update();
    });

    indoorPlus?.addEventListener('click', () => {
      indoorUnits += 1;
      update();
    });

    outdoorMinus?.addEventListener('click', () => {
      outdoorUnits -= 1;
      update();
    });

    outdoorPlus?.addEventListener('click', () => {
      outdoorUnits += 1;
      update();
    });

    section.querySelectorAll('[data-gads-package]').forEach(button => {
      button.addEventListener('click', () => {
        indoorUnits = Number(button.dataset.gadsPackage || 1);
        if (outdoorUnits > indoorUnits) outdoorUnits = indoorUnits;
        update();

        builder?.scrollIntoView({
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
          block: 'center'
        });

        pushDataLayer('seq_google_landing_package_selected', {
          indoor_units: indoorUnits,
          outdoor_units: outdoorUnits
        });
      });
    });

    section.querySelectorAll('a[href^="#"]').forEach(link => {
      link.addEventListener('click', event => {
        const selector = link.getAttribute('href');
        if (!selector || selector === '#') return;
        const target = document.querySelector(selector);
        if (!target) return;

        event.preventDefault();
        target.scrollIntoView({
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
          block: 'start'
        });
      });
    });

    section.querySelectorAll('[data-gads-booking-cta]').forEach(cta => {
      cta.addEventListener('click', () => {
        pushDataLayer('seq_google_landing_booking_click', {
          booking_source: cta.dataset.bookingSource || 'google_landing',
          indoor_units: indoorUnits,
          outdoor_units: outdoorUnits,
          estimated_value: (indoorSubtotal(indoorUnits) + outdoorUnits * outdoorPrice) / 100,
          currency: 'AUD'
        });
      });
    });

    section.querySelectorAll('[data-gads-phone]').forEach(link => {
      link.addEventListener('click', () => {
        pushDataLayer('seq_google_landing_phone_click', {
          link_url: link.href
        });
      });
    });

    section.querySelectorAll('.seq-gads-faq details').forEach(details => {
      details.addEventListener('toggle', () => {
        if (!details.open) return;
        section.querySelectorAll('.seq-gads-faq details[open]').forEach(openDetails => {
          if (openDetails !== details) openDetails.removeAttribute('open');
        });
      });
    });

    update();

    pushDataLayer('seq_google_landing_view', {
      page_location: window.location.href,
      page_path: window.location.pathname
    });
  };

  const init = scope => {
    if (scope instanceof HTMLElement && scope.matches('[data-seq-google-landing]')) {
      initLanding(scope);
    }
    scope.querySelectorAll?.('[data-seq-google-landing]').forEach(initLanding);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', event => init(event.target));
})();
